import { useMemo, useState } from 'react';
import type { CellValueChangedEvent, ColDef, ICellRendererParams } from 'ag-grid-community';
import { NJButton, NJIconButton, NJInlineMessage, NJTooltip } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import { Size2MixAccordion, Size2MixExpandCollapseBar, Size2MixSection, useSectionsToggle } from '@shared/size2mix-shell';
import { CONSTRAINT_LABELS, TOOLTIPS } from './fixtures';
import FuelContractPanel from './FuelContractPanel';
import { useFuelsMutation } from './hooks';
import { EnrRatePanel, PollutantPanel } from './RatePanels';
import { CONSTRAINT_KEYS, type EnrRate, type FuelContract, type FuelsCollection, type FuelsData, type Pollutant } from './types';
import { DeleteConfirmModal, EditDeleteActions, TableTitle, formatValue, nemoFlexWidths, parseNonNegative } from './ui';
import VariablePricesModal from './VariablePricesModal';
import './contracts.css';

const SECTIONS = ['fuels', 'enr', 'pollutants'] as const;
const TABLE_DEFAULTS: ColDef = { flex: 1, resizable: true, sortable: true, wrapHeaderText: true, autoHeaderHeight: true };
const READONLY = 'production-contracts-readonly';
const EDITABLE = 'production-contracts-editable';
const EDITABLE_CENTER = `${EDITABLE} production-contracts-center`;
// Nemo centres the headers of the dynamic (EnR rate / pollutant) columns only.
const HEADER_CENTER = 'production-contracts-header-center';

type Panel =
    | { kind: 'contract'; item?: FuelContract }
    | { kind: 'enrRates'; item?: EnrRate }
    | { kind: 'pollutants'; item?: Pollutant };
type Deleting = { collection: FuelsCollection; id: string; name: string };
type PivotRow = { id: string; label: string; isActionsRow?: boolean } & Record<string, unknown>;

/** Nemo `CalorificValueHeader`: label + 14px `info` icon (tooltip below), inheriting the header colour. */
const infoHeader = (label: string, tooltip: string) => () => (
    <div className="production-contracts-header">
        <span>{label}</span>
        <NJTooltip text={tooltip} hasArrow placement="bottom">
            <span className="material-icons-outlined production-contracts-info" aria-hidden="true">info</span>
        </NJTooltip>
    </div>
);

const firstColumn = (headerName: string): ColDef<PivotRow> => ({
    field: 'label',
    headerName,
    pinned: 'left',
    flex: 2,
    cellClass: ({ data }) => (data?.isActionsRow ? 'production-contracts-actions-row' : READONLY),
});

export default function FuelsPage({ data }: { data: FuelsData }) {
    const { bind, expandAll, collapseAll } = useSectionsToggle(SECTIONS);
    const mutation = useFuelsMutation();
    const [panel, setPanel] = useState<Panel | null>(null);
    const [deleting, setDeleting] = useState<Deleting | null>(null);
    const [pricesContractId, setPricesContractId] = useState<string | null>(null);
    const { contracts, enrRates, pollutants } = data;
    const pricesContract = contracts.find((contract) => contract.id === pricesContractId);

    const update = (collection: FuelsCollection, id: string, patch: object, onDone?: () => void) => mutation.mutate({ kind: 'update', collection, id, patch }, { onSuccess: onDone });
    const closePanel = () => { setPanel(null); mutation.reset(); };
    const panelError = panel && mutation.isError ? mutation.error.message : undefined;
    const tableError = !panel && !deleting && mutation.isError ? mutation.error.message : undefined;

    // ── Contrats de combustibles ────────────────────────────────────────────
    const contractColumns = useMemo<ColDef<FuelContract>[]>(() => [
        { field: 'name', headerName: 'Nom', cellClass: READONLY, filter: true, flex: 3 },
        { field: 'linkedUnits', headerName: 'Équipements de production liés', cellClass: READONLY, flex: 2 },
        { field: 'fixedPrice', headerName: 'Prix fixe (€/u)', editable: true, cellClass: EDITABLE_CENTER, flex: 2, valueParser: parseNonNegative, valueFormatter: ({ value }) => formatValue(value) },
        {
            headerName: 'Prix variable (€/u)',
            flex: 2,
            sortable: false,
            cellClass: 'production-contracts-center',
            cellRenderer: ({ data: row }: ICellRendererParams<FuelContract>) => row && (
                <NJTooltip text={TOOLTIPS.variablePrice} hasArrow placement="top">
                    <NJIconButton icon="open_in_new" label="" aria-label={TOOLTIPS.variablePrice} variant="tertiary" onClick={() => setPricesContractId(row.id)} />
                </NJTooltip>
            ),
        },
        {
            field: 'calorificValue',
            headerName: 'Valeur calorifique (J/u)',
            headerComponent: infoHeader('Valeur calorifique (J/u)', TOOLTIPS.calorificValue),
            editable: true,
            cellClass: EDITABLE,
            flex: 2,
            valueParser: (params) => parseNonNegative(params) || params.oldValue,
        },
        { field: 'description', headerName: 'Description', editable: true, cellClass: EDITABLE, flex: 3, valueFormatter: ({ value }) => formatValue(value) },
        {
            headerName: 'Actions',
            flex: 2,
            sortable: false,
            cellRenderer: ({ data: row }: ICellRendererParams<FuelContract>) => row && (
                <EditDeleteActions label={row.name} onEdit={() => setPanel({ kind: 'contract', item: row })} onDelete={() => setDeleting({ collection: 'contracts', id: row.id, name: row.name })} />
            ),
        },
    ], []);

    const constraintColumns = useMemo<ColDef<FuelContract>[]>(() => [
        { field: 'name', headerName: 'Nom du contrat', cellClass: READONLY, filter: true, flex: 2 },
        ...CONSTRAINT_KEYS.map((key): ColDef<FuelContract> => ({
            colId: key,
            headerName: CONSTRAINT_LABELS[key],
            editable: true,
            cellClass: EDITABLE_CENTER,
            valueGetter: ({ data: row }) => row?.constraints[key] ?? null,
            valueSetter: (params) => { params.data.constraints = { ...params.data.constraints, [key]: params.newValue }; return true; },
            valueParser: parseNonNegative,
            valueFormatter: ({ value }) => formatValue(value),
        })),
    ], []);

    const onContractChange = (event: CellValueChangedEvent<FuelContract>) => {
        if (event.newValue === event.oldValue) return;
        const colId = event.column.getColId();
        const patch = (CONSTRAINT_KEYS as readonly string[]).includes(colId) ? { constraints: { [colId]: event.newValue } } : { [colId]: event.newValue ?? (colId === 'description' ? '' : null) };
        update('contracts', event.data.id, patch);
    };

    // ── Taux EnR ────────────────────────────────────────────────────────────
    const enrRows: PivotRow[] = contracts.map((contract) => ({ id: contract.id, label: contract.name, ...Object.fromEntries(enrRates.map((rate) => [rate.id, rate.shares[contract.id] ?? 0])) }));
    const enrColumns: ColDef<PivotRow>[] = [
        firstColumn('Contrat'),
        ...enrRates.map((rate): ColDef<PivotRow> => ({
            field: rate.id,
            headerName: rate.name,
            headerClass: HEADER_CENTER,
            editable: ({ data: row }) => !row?.isActionsRow,
            cellClass: ({ data: row }) => (row?.isActionsRow ? 'production-contracts-actions-row' : EDITABLE_CENTER),
            valueParser: (params) => { const parsed = parseNonNegative(params); return typeof parsed === 'number' ? Math.min(100, parsed) : 0; },
            valueFormatter: ({ value, data: row }) => (row?.isActionsRow ? '' : `${Number(Number(value ?? 0).toFixed(2))}%`),
            cellRenderer: ({ data: row, valueFormatted }: ICellRendererParams<PivotRow>) => (row?.isActionsRow
                ? <EditDeleteActions label={rate.name} onEdit={() => setPanel({ kind: 'enrRates', item: rate })} onDelete={() => setDeleting({ collection: 'enrRates', id: rate.id, name: rate.name })} />
                : valueFormatted),
        })),
    ];
    const enrThresholdRows: PivotRow[] = (['min', 'max'] as const).map((bound) => ({ id: bound, label: bound === 'min' ? 'Min' : 'Max', ...Object.fromEntries(enrRates.map((rate) => [rate.id, rate[bound]])) }));
    const enrThresholdColumns: ColDef<PivotRow>[] = [
        firstColumn('Taux EnR'),
        ...enrRates.map((rate): ColDef<PivotRow> => ({
            field: rate.id,
            headerName: rate.name,
            headerClass: HEADER_CENTER,
            editable: true,
            cellClass: EDITABLE_CENTER,
            valueParser: (params) => { const parsed = parseNonNegative(params); return typeof parsed === 'number' ? Math.min(100, parsed) : parsed; },
            valueFormatter: ({ value }) => (value === null || value === undefined ? '-' : `${value}%`),
        })),
    ];

    // ── Polluants ───────────────────────────────────────────────────────────
    const pollutantRows: PivotRow[] = contracts.map((contract) => ({ id: contract.id, label: contract.name, ...Object.fromEntries(pollutants.map((p) => [p.id, p.emissions[contract.id] ?? null])) }));
    const pollutantColumns: ColDef<PivotRow>[] = [
        firstColumn('Contrat'),
        ...pollutants.map((pollutant): ColDef<PivotRow> => ({
            field: pollutant.id,
            headerName: `${pollutant.name} (kg/J)`,
            headerClass: HEADER_CENTER,
            editable: ({ data: row }) => !row?.isActionsRow,
            cellClass: ({ data: row }) => (row?.isActionsRow ? 'production-contracts-actions-row' : EDITABLE_CENTER),
            valueParser: parseNonNegative,
            valueFormatter: ({ value, data: row }) => (row?.isActionsRow ? '' : formatValue(value)),
            cellRenderer: ({ data: row, valueFormatted }: ICellRendererParams<PivotRow>) => (row?.isActionsRow
                ? <EditDeleteActions label={pollutant.name} onEdit={() => setPanel({ kind: 'pollutants', item: pollutant })} onDelete={() => setDeleting({ collection: 'pollutants', id: pollutant.id, name: pollutant.name })} />
                : valueFormatted),
        })),
    ];
    const pollutantThresholdRows: PivotRow[] = [
        { id: 'maxThreshold', label: pollutants.some((p) => p.maxThreshold !== null) ? 'Seuil max (kg)' : 'Seuil', ...Object.fromEntries(pollutants.map((p) => [p.id, p.maxThreshold])) },
        { id: 'fixedPrice', label: 'Prix fixe (€/kg)', ...Object.fromEntries(pollutants.map((p) => [p.id, p.fixedPrice])) },
        { id: 'variablePrice', label: 'Prix variable (€/J)' },
    ];
    const pollutantThresholdColumns: ColDef<PivotRow>[] = [
        firstColumn('Paramètres'),
        ...pollutants.map((pollutant): ColDef<PivotRow> => ({
            field: pollutant.id,
            headerName: pollutant.name,
            headerClass: HEADER_CENTER,
            // Nemo: only the « Prix fixe » row is editable inline (2 decimals).
            editable: ({ data: row }) => row?.id === 'fixedPrice',
            cellClass: ({ data: row }) => (row?.id === 'fixedPrice' ? EDITABLE_CENTER : 'production-contracts-center'),
            valueParser: (params) => { const parsed = parseNonNegative(params); return typeof parsed === 'number' ? Math.round(parsed * 100) / 100 : parsed; },
            valueFormatter: ({ value }) => (typeof value === 'number' ? value.toFixed(2) : formatValue(value)),
            cellRenderer: ({ data: row, valueFormatted }: ICellRendererParams<PivotRow>) => (row?.id === 'variablePrice'
                ? (
                    <NJTooltip text="Prix variable" hasArrow placement="top">
                        <NJIconButton icon="open_in_new" label="" aria-label="Prix variable" variant="tertiary" isDisabled />
                    </NJTooltip>
                )
                : valueFormatted),
        })),
    ];

    const pivotChange = (collection: 'enrRates' | 'pollutants', nested: 'shares' | 'emissions') => (event: CellValueChangedEvent<PivotRow>) => {
        if (event.newValue === event.oldValue || event.data.isActionsRow) return;
        update(collection, event.column.getColId(), { [nested]: { [event.data.id]: event.newValue } });
    };
    const thresholdChange = (collection: 'enrRates' | 'pollutants') => (event: CellValueChangedEvent<PivotRow>) => {
        if (event.newValue === event.oldValue) return;
        update(collection, event.column.getColId(), { [event.data.id]: event.newValue });
    };

    const names = (collection: FuelsCollection) => data[collection].map((item) => item.name);
    const actionsRow: PivotRow = { id: 'actions', label: 'Actions', isActionsRow: true };

    return (
        <div className="production-contracts">
            {tableError && <NJInlineMessage variant="error" title="Modification impossible">{tableError}</NJInlineMessage>}
            <Size2MixAccordion>
                <Size2MixExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />

                <Size2MixSection togglePosition="trailing" title={<span>Combustibles</span>} icon="local_fire_department" {...bind('fuels')}>
                    <div className="production-contracts-body">
                        <div><NJButton label="Créer un nouveau contrat" icon="add" onClick={() => setPanel({ kind: 'contract' })} /></div>
                        <div>
                            <TableTitle title="Contrats de combustibles" />
                            <DataTable<FuelContract> testId="fuel-contracts-table" header={contractColumns} data={contracts} isPaginated paginationPageSize={15} defaultColDef={TABLE_DEFAULTS} onGridReady={nemoFlexWidths} onCellValueChanged={onContractChange} />
                        </div>
                        <div>
                            <TableTitle title="Contraintes de combustible" tooltip={TOOLTIPS.fuelConstraints} />
                            <DataTable<FuelContract> testId="fuel-constraints-table" header={constraintColumns} data={contracts} isPaginated paginationPageSize={15} defaultColDef={TABLE_DEFAULTS} onGridReady={nemoFlexWidths} onCellValueChanged={onContractChange} />
                        </div>
                    </div>
                </Size2MixSection>

                <Size2MixSection togglePosition="trailing" title={<span>Paramétrage des taux EnR</span>} icon="shuffle" {...bind('enr')}>
                    <div className="production-contracts-body">
                        <div><NJButton label="Créer un taux EnR" icon="add" onClick={() => setPanel({ kind: 'enrRates' })} /></div>
                        {enrRates.length === 0 ? (
                            <NJInlineMessage variant="information">Tableau indisponible : aucun taux EnR n'a été paramétré pour ce réseau.</NJInlineMessage>
                        ) : (
                            <>
                                <div>
                                    <TableTitle title="Contribution des contrats combustibles aux taux EnR" tooltip={TOOLTIPS.enrContributions} />
                                    <DataTable<PivotRow> testId="enr-contributions-table" header={enrColumns} data={enrRows} pinnedBottomRowData={[actionsRow]} defaultColDef={TABLE_DEFAULTS} onGridReady={nemoFlexWidths} onCellValueChanged={pivotChange('enrRates', 'shares')} />
                                </div>
                                <div>
                                    <TableTitle title="Seuils des taux EnR" tooltip={TOOLTIPS.enrThresholds} />
                                    <DataTable<PivotRow> testId="enr-thresholds-table" header={enrThresholdColumns} data={enrThresholdRows} defaultColDef={TABLE_DEFAULTS} onGridReady={nemoFlexWidths} onCellValueChanged={thresholdChange('enrRates')} />
                                </div>
                            </>
                        )}
                    </div>
                </Size2MixSection>

                <Size2MixSection togglePosition="trailing" title={<span>Polluants</span>} icon="cloud" {...bind('pollutants')}>
                    <div className="production-contracts-body">
                        <div><NJButton label="Créer un polluant" icon="add" onClick={() => setPanel({ kind: 'pollutants' })} /></div>
                        {pollutants.length === 0 ? (
                            <NJInlineMessage variant="information">Tableau indisponible : aucun polluant n'a été paramétré pour ce réseau.</NJInlineMessage>
                        ) : (
                            <>
                                <div>
                                    <TableTitle title="Liste des polluants et facteurs d'émission" />
                                    <DataTable<PivotRow> testId="pollutants-table" header={pollutantColumns} data={pollutantRows} pinnedBottomRowData={[actionsRow]} defaultColDef={TABLE_DEFAULTS} onGridReady={nemoFlexWidths} onCellValueChanged={pivotChange('pollutants', 'emissions')} />
                                </div>
                                <div>
                                    <TableTitle title="Seuils des émissions polluantes" />
                                    <DataTable<PivotRow> testId="pollutant-thresholds-table" header={pollutantThresholdColumns} data={pollutantThresholdRows} defaultColDef={TABLE_DEFAULTS} onGridReady={nemoFlexWidths} onCellValueChanged={thresholdChange('pollutants')} />
                                </div>
                            </>
                        )}
                    </div>
                </Size2MixSection>
            </Size2MixAccordion>

            {panel?.kind === 'contract' && (
                <FuelContractPanel
                    contract={panel.item}
                    existingNames={names('contracts')}
                    error={panelError}
                    isPending={mutation.isPending}
                    onClose={closePanel}
                    onSubmit={(value) => (panel.item
                        ? mutation.mutate({ kind: 'update', collection: 'contracts', id: panel.item.id, patch: value }, { onSuccess: closePanel })
                        : mutation.mutate({ kind: 'create', collection: 'contracts', input: value }, { onSuccess: closePanel }))}
                />
            )}
            {panel?.kind === 'enrRates' && (
                <EnrRatePanel
                    rate={panel.item}
                    contracts={contracts}
                    existingNames={names('enrRates')}
                    error={panelError}
                    isPending={mutation.isPending}
                    onClose={closePanel}
                    onSubmit={(value) => (panel.item
                        ? mutation.mutate({ kind: 'update', collection: 'enrRates', id: panel.item.id, patch: value }, { onSuccess: closePanel })
                        : mutation.mutate({ kind: 'create', collection: 'enrRates', input: value }, { onSuccess: closePanel }))}
                />
            )}
            {panel?.kind === 'pollutants' && (
                <PollutantPanel
                    pollutant={panel.item}
                    contracts={contracts}
                    existingNames={names('pollutants')}
                    error={panelError}
                    isPending={mutation.isPending}
                    onClose={closePanel}
                    onSubmit={(value) => (panel.item
                        ? mutation.mutate({ kind: 'update', collection: 'pollutants', id: panel.item.id, patch: value }, { onSuccess: closePanel })
                        : mutation.mutate({ kind: 'create', collection: 'pollutants', input: value }, { onSuccess: closePanel }))}
                />
            )}
            {pricesContract && (
                <VariablePricesModal
                    contract={pricesContract}
                    isPending={mutation.isPending}
                    onClose={() => setPricesContractId(null)}
                    onValidate={(variablePrices) => update('contracts', pricesContract.id, { variablePrices }, () => setPricesContractId(null))}
                />
            )}
            {deleting && (
                <DeleteConfirmModal
                    id="delete-fuels-item"
                    title={deleting.collection === 'enrRates' ? 'Confirmation de suppression du taux EnR' : 'Confirmation de suppression'}
                    isPending={mutation.isPending}
                    onCancel={() => setDeleting(null)}
                    onConfirm={() => mutation.mutate({ kind: 'delete', collection: deleting.collection, id: deleting.id }, { onSettled: () => setDeleting(null) })}
                >
                    {deleting.collection === 'contracts' && <>Êtes-vous bien certain de vouloir supprimer le contrat <strong>{deleting.name}</strong> ?</>}
                    {deleting.collection === 'enrRates' && <>Supprimer le taux EnR <strong>{deleting.name}</strong> ?</>}
                    {deleting.collection === 'pollutants' && <>Êtes-vous sûr de vouloir supprimer le polluant <strong>{deleting.name}</strong> ?</>}
                </DeleteConfirmModal>
            )}
        </div>
    );
}
